import React, { FunctionComponent, useLayoutEffect } from 'react';
import { useColors } from './brandingState';
import { BrandingColors, brandingColorKebab, brandingColorNames, brandingColorVar } from './branding-types';

interface BrandingProps {
  children: React.ReactNode;
}

// hexToChannels converts a hex color (#RGB or #RRGGBB) to space separated RGB channels (e.g. "40 120 253").
export const hexToChannels = (hex: string): string => {
  let value = hex.replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const int = parseInt(value, 16);
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
};

// applyBrandingColors writes the theme to CSS variables on the given element.  Two variables are set per color:
//  * --color-<name>: RGB channels, used by the Tailwind colors (supports opacity modifiers like bg-primary/10).
//  * --<name>-color: the hex value, for things outside Tailwind that need a plain color (e.g. the Mux player).
export const applyBrandingColors = (element: HTMLElement, colors: BrandingColors) => {
  brandingColorNames.forEach((name) => {
    element.style.setProperty(brandingColorVar(name), hexToChannels(colors[name]));
    element.style.setProperty(`--${brandingColorKebab(name)}-color`, colors[name]);
  });
};

// Branding is a wrapper that applies the branding styles via css variables to the document.  A layout effect is used
// so the colors are applied before the first paint (avoiding a flash of unstyled colors).
export const Branding: FunctionComponent<BrandingProps> = ({ children }) => {
  const colors = useColors();

  useLayoutEffect(() => {
    applyBrandingColors(document.documentElement, colors);
  }, [colors]);

  return <>{children}</>;
};
